import { createContext, useContext } from "react";

// Cookie consent categories under GDPR. "necessary" cookies are always active
// (required for the site to work) and cannot be disabled by the visitor.
export type ConsentCategory = "analytics" | "marketing";

// Effective consent state once the visitor has made a choice.
export type ConsentState = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

// Shape persisted to localStorage. Versioned so that if the cookie policy
// changes we can invalidate old consent and ask again.
export type StoredConsent = {
  version: number;
  updatedAt: string; // ISO 8601 UTC
  analytics: boolean;
  marketing: boolean;
};

export const CONSENT_STORAGE_KEY = "enkey-cookie-consent";
// Bump this when the cookie policy materially changes to re-prompt visitors.
export const CONSENT_VERSION = 5;

export type CookieConsentContextValue = {
  // null until the visitor has explicitly accepted / rejected / saved.
  consent: ConsentState | null;
  isDecided: boolean;
  preferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (categories: {
    analytics: boolean;
    marketing: boolean;
  }) => void;
  // Convenience check used to gate non-essential scripts / embeds.
  hasConsent: (category: ConsentCategory) => boolean;
};

export const CookieConsentContext =
  createContext<CookieConsentContextValue | null>(null);

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider",
    );
  }
  return ctx;
}
